import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  FileArchive,
  FileAudio,
  FileSpreadsheet,
  FileText,
  FileType,
  Loader2,
  Play,
  Plus,
  Send,
  X,
} from "lucide-react";

import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { cn } from "../../lib/utils";
import { ACCEPT_UPLOAD, extensaoDoArquivo } from "../../constants/uploads";

// Prévia do que vai ser enviado, numa janela.
//
// Antes daqui, o que aparecia era o nome do arquivo e — só quando fosse
// imagem, e só a primeira — uma miniatura de 40 px na barra. Anexar quatro
// fotos e mandar a errada era questão de tempo: "IMG_20240113_0912.jpg" não
// diz nada, e não havia como tirar UM arquivo da lista sem recomeçar.
//
// Agora abre uma janela com TODOS os arquivos escolhidos: o de cima aparece
// grande (a foto, o vídeo tocando, a página do PDF, o áudio com controle) e os
// outros ficam na fita embaixo, cada um com o seu X. Vale igual em conversa de
// contato e de grupo — é a mesma barra de mensagem nas duas.

const EXT_PLANILHA = [".xls", ".xlsx", ".csv", ".ods"];
const EXT_COMPACTADO = [".zip", ".rar", ".7z", ".gz", ".tgz"];
const EXT_TEXTO = [".txt", ".md", ".json", ".xml", ".rtf", ".doc", ".docx"];

const tipoDoArquivo = (arquivo) => {
  const mime = arquivo?.type || "";
  const ext = extensaoDoArquivo(arquivo?.name);

  if (mime.startsWith("image/")) return "imagem";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (mime === "application/pdf" || ext === ".pdf") return "pdf";
  if (EXT_PLANILHA.includes(ext)) return "planilha";
  if (EXT_COMPACTADO.includes(ext)) return "compactado";
  if (EXT_TEXTO.includes(ext)) return "texto";
  return "arquivo";
};

const ICONES = {
  audio: FileAudio,
  pdf: FileText,
  planilha: FileSpreadsheet,
  compactado: FileArchive,
  texto: FileType,
  arquivo: FileType,
};

export const formatarTamanho = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
};

const Icone = ({ tipo, className }) => {
  const Componente = ICONES[tipo] || FileType;
  return <Componente className={className} />;
};

const PreviaDeAnexos = ({
  arquivos,
  aoMudar,
  aoAdicionar,
  legenda,
  aoMudarLegenda,
  aoEnviar,
  aoCancelar,
  enviando,
}) => {
  const [atual, setAtual] = useState(0);
  const [arrastando, setArrastando] = useState(false);
  const inputRef = useRef(null);

  // Uma URL por arquivo, criada uma vez e revogada quando a lista muda.
  // Criar dentro do render vazaria uma URL a cada quadro.
  const itens = useMemo(
    () =>
      arquivos.map((arquivo) => ({
        arquivo,
        tipo: tipoDoArquivo(arquivo),
        url: window.URL.createObjectURL(arquivo),
      })),
    [arquivos],
  );

  useEffect(
    () => () => itens.forEach((item) => window.URL.revokeObjectURL(item.url)),
    [itens],
  );

  const total = arquivos.reduce((soma, arquivo) => soma + arquivo.size, 0);
  const emFoco = itens[Math.min(atual, itens.length - 1)];

  // Tirar um arquivo não pode deslocar a seleção para outro por acidente,
  // nem deixar o índice apontando para fora da lista.
  const remover = (indice) => {
    const restantes = arquivos.filter((_, i) => i !== indice);

    if (!restantes.length) {
      aoCancelar();
      return;
    }

    aoMudar(restantes);
    setAtual((posicao) =>
      Math.min(posicao > indice ? posicao - 1 : posicao, restantes.length - 1),
    );
  };

  // Quem acabou de anexar quer ver o que anexou — e não continuar olhando o
  // primeiro arquivo da lista.
  const acrescentar = (novos) => {
    if (!novos.length) return;
    aoAdicionar(novos);
    setAtual(arquivos.length);
  };

  const soltarArquivos = (e) => {
    e.preventDefault();
    setArrastando(false);
    acrescentar(Array.from(e.dataTransfer?.files || []));
  };

  const navegar = useCallback(
    (passo) => {
      setAtual((posicao) => {
        const proximo = posicao + passo;
        if (proximo < 0 || proximo >= itens.length) return posicao;
        return proximo;
      });
    },
    [itens.length],
  );

  // Setas do teclado passam de um arquivo para o outro; digitar a legenda não
  // pode virar navegação.
  useEffect(() => {
    const aoTeclar = (e) => {
      const campo = e.target?.tagName;
      if (campo === "TEXTAREA" || campo === "INPUT") return;
      if (e.key === "ArrowRight") navegar(1);
      if (e.key === "ArrowLeft") navegar(-1);
    };

    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [navegar]);

  return (
    <Dialog
      open={arquivos.length > 0}
      onOpenChange={(aberto) => {
        if (!aberto && !enviando) aoCancelar();
      }}
    >
      <DialogContent
        className={cn(
          "flex max-h-[90vh] max-w-3xl flex-col gap-3",
          arrastando && "ring-2 ring-primary",
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setArrastando(true);
        }}
        onDragLeave={() => setArrastando(false)}
        onDrop={soltarArquivos}
      >
        <DialogTitle className="pr-8 text-base">
          Enviar {arquivos.length === 1 ? "arquivo" : `${arquivos.length} arquivos`}
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            {formatarTamanho(total)}
          </span>
        </DialogTitle>

        {/* O arquivo em foco, grande. É aqui que se confere se é mesmo o
            que se quer mandar antes de mandar. */}
        <div className="relative flex min-h-[38vh] flex-1 items-center justify-center overflow-hidden rounded-lg bg-muted/40">
          {emFoco?.tipo === "imagem" && (
            <img
              src={emFoco.url}
              alt={emFoco.arquivo.name}
              className="max-h-[52vh] w-auto object-contain"
            />
          )}

          {emFoco?.tipo === "video" && (
            <video
              key={emFoco.url}
              src={emFoco.url}
              controls
              playsInline
              className="max-h-[52vh] w-full"
            />
          )}

          {emFoco?.tipo === "audio" && (
            <div className="flex w-full flex-col items-center gap-3 p-8">
              <FileAudio className="h-10 w-10 text-muted-foreground" />
              <audio src={emFoco.url} controls className="w-full max-w-md" />
            </div>
          )}

          {emFoco?.tipo === "pdf" && (
            /* Não há biblioteca de PDF no projeto: quem desenha a página é o
               leitor do próprio navegador. */
            <iframe
              key={emFoco.url}
              src={emFoco.url}
              title={emFoco.arquivo.name}
              className="h-[52vh] w-full border-0 bg-white"
            />
          )}

          {emFoco &&
            !["imagem", "video", "audio", "pdf"].includes(emFoco.tipo) && (
              <div className="flex flex-col items-center gap-2 p-10 text-center text-muted-foreground">
                <Icone tipo={emFoco.tipo} className="h-12 w-12" />
                <p className="text-sm">
                  Este tipo de arquivo não abre aqui — vai anexado do mesmo
                  jeito.
                </p>
              </div>
            )}

          {itens.length > 1 && (
            <>
              <Button
                variant="secondary"
                size="icon"
                aria-label="Arquivo anterior"
                onClick={() => navegar(-1)}
                disabled={atual <= 0}
                className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full shadow-sm"
              >
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                aria-label="Próximo arquivo"
                onClick={() => navegar(1)}
                disabled={atual >= itens.length - 1}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full shadow-sm"
              >
                <ChevronRight className="h-5 w-5" />
              </Button>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span className="truncate" title={emFoco?.arquivo.name}>
            {emFoco?.arquivo.name}
            {" · "}
            {emFoco && formatarTamanho(emFoco.arquivo.size)}
          </span>
          {itens.length > 1 && (
            <span className="shrink-0">
              {atual + 1} de {itens.length}
            </span>
          )}
        </div>

        {/* Fita com TUDO que vai junto: o que está em foco, o que já foi
            escolhido e o botão de acrescentar mais. */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {itens.map((item, indice) => (
            <div
              key={`${item.arquivo.name}-${item.arquivo.size}-${indice}`}
              className="relative h-16 w-16 shrink-0"
            >
              <button
                type="button"
                onClick={() => setAtual(indice)}
                title={`${item.arquivo.name} — ${formatarTamanho(item.arquivo.size)}`}
                className={cn(
                  "relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-lg border bg-background text-muted-foreground",
                  indice === atual && "ring-2 ring-primary",
                )}
              >
                {item.tipo === "imagem" && (
                  <img
                    src={item.url}
                    alt={item.arquivo.name}
                    className="h-full w-full object-cover"
                  />
                )}

                {item.tipo === "video" && (
                  <>
                    {/* O fragmento #t=0.1 faz o navegador desenhar o primeiro
                        quadro sem precisar dar play. */}
                    <video
                      src={`${item.url}#t=0.1`}
                      muted
                      playsInline
                      preload="metadata"
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                      <Play className="h-5 w-5 fill-white text-white" />
                    </span>
                  </>
                )}

                {item.tipo === "pdf" && (
                  <>
                    <iframe
                      src={`${item.url}#toolbar=0&navpanes=0&view=FitH`}
                      title={item.arquivo.name}
                      className="pointer-events-none h-full w-full scale-[1.4] border-0 bg-white"
                    />
                    <span className="absolute inset-x-0 bottom-0 bg-black/60 px-1 py-0.5 text-[10px] font-medium text-white">
                      PDF
                    </span>
                  </>
                )}

                {!["imagem", "video", "pdf"].includes(item.tipo) && (
                  <>
                    <Icone tipo={item.tipo} className="h-6 w-6" />
                    <span className="mt-0.5 max-w-full truncate px-1 text-[10px] uppercase">
                      {extensaoDoArquivo(item.arquivo.name).replace(".", "") ||
                        "arquivo"}
                    </span>
                  </>
                )}
              </button>

              <button
                type="button"
                aria-label={`Tirar ${item.arquivo.name}`}
                title="Tirar da lista"
                onClick={() => remover(indice)}
                disabled={enviando}
                className="absolute -right-1 -top-1 rounded-full bg-background p-0.5 text-muted-foreground shadow-sm ring-1 ring-border hover:text-destructive disabled:opacity-50"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={enviando}
            title="Anexar mais arquivos"
            aria-label="Anexar mais arquivos"
            className="flex h-16 w-16 shrink-0 flex-col items-center justify-center gap-0.5 rounded-lg border border-dashed text-muted-foreground hover:bg-muted/50 disabled:opacity-50"
          >
            <Plus className="h-5 w-5" />
            <span className="text-[10px]">Adicionar</span>
          </button>

          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            accept={ACCEPT_UPLOAD}
            onChange={(e) => {
              acrescentar(Array.from(e.target.files || []));
              e.target.value = "";
            }}
          />
        </div>

        <div className="flex items-end gap-2">
          <textarea
            rows={1}
            value={legenda}
            onChange={(e) => aoMudarLegenda(e.target.value)}
            disabled={enviando}
            placeholder="Escreva uma legenda (opcional)"
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                aoEnviar(e);
              }
            }}
            className="max-h-24 min-w-0 flex-1 resize-none rounded-lg border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-60"
          />

          <Button
            variant="ghost"
            onClick={aoCancelar}
            disabled={enviando}
            className="text-muted-foreground"
          >
            Cancelar
          </Button>

          <Button onClick={aoEnviar} disabled={enviando} className="gap-2">
            {enviando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Enviar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PreviaDeAnexos;
